export const EVENTQ_WEEKDAY_LABELS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] as const;

export interface EventqCalendarCell {
  date: Date;
  day: number;
  inMonth: boolean;
  isSelected: boolean;
  isToday: boolean;
  disabled: boolean;
}

export interface EventqZonedDateTimeParts {
  year: number;
  month: number;
  day: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export function isSameCalendarDay(left: Date, right: Date, timeZone?: string | null): boolean {
  if (timeZone) {
    const leftParts = getZonedParts(left, timeZone);
    const rightParts = getZonedParts(right, timeZone);
    return (
      leftParts.year === rightParts.year &&
      leftParts.month === rightParts.month &&
      leftParts.day === rightParts.day
    );
  }
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

/** True when no instant on this calendar day falls within `[min, max]`. */
export function isCalendarDayDisabled(
  day: Date,
  min: Date | null | undefined,
  max: Date | null | undefined,
  timeZone?: string | null,
): boolean {
  if (!min && !max) {
    return false;
  }

  let dayStart: Date;
  let dayEnd: Date;
  if (timeZone) {
    const parts = getZonedParts(day, timeZone);
    dayStart = zonedPartsToUtcDate(
      { ...parts, hours: 0, minutes: 0, seconds: 0 },
      timeZone,
    );
    dayEnd = new Date(
      zonedPartsToUtcDate({ ...parts, hours: 23, minutes: 59, seconds: 59 }, timeZone).getTime() +
        999,
    );
  } else {
    dayStart = new Date(day);
    dayStart.setHours(0, 0, 0, 0);
    dayEnd = new Date(day);
    dayEnd.setHours(23, 59, 59, 999);
  }

  if (min && dayEnd.getTime() < min.getTime()) {
    return true;
  }
  if (max && dayStart.getTime() > max.getTime()) {
    return true;
  }
  return false;
}

export function clampDateToRange(
  date: Date,
  min: Date | null | undefined,
  max: Date | null | undefined,
): Date {
  let next = new Date(date);
  if (min && next.getTime() < min.getTime()) {
    next = new Date(min);
  }
  if (max && next.getTime() > max.getTime()) {
    next = new Date(max);
  }
  return next;
}

export function buildCalendarWeeks(
  viewMonth: Date,
  selected: Date | null,
  min?: Date | null,
  max?: Date | null,
  timeZone?: string | null,
): EventqCalendarCell[][] {
  if (timeZone) {
    return buildZonedCalendarWeeks(viewMonth, selected, min, max, timeZone);
  }

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const start = new Date(year, month, 1 - new Date(year, month, 1).getDay());
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const weeks: EventqCalendarCell[][] = [];
  const cursor = new Date(start);

  for (let weekIndex = 0; weekIndex < 6; weekIndex++) {
    const week: EventqCalendarCell[] = [];
    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      const cellDate = new Date(cursor);
      cellDate.setHours(0, 0, 0, 0);
      week.push({
        date: cellDate,
        day: cellDate.getDate(),
        inMonth: cellDate.getMonth() === month,
        isSelected: selected ? isSameCalendarDay(cellDate, selected) : false,
        isToday: isSameCalendarDay(cellDate, today),
        disabled: isCalendarDayDisabled(cellDate, min, max),
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
  }

  return weeks;
}

export function formatMonthYearLabel(viewMonth: Date): string {
  return viewMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export function padTimeUnit(value: number): string {
  return String(value).padStart(2, '0');
}

export function clampTimeUnit(value: number, max: number): number {
  if (value < 0) {
    return max;
  }
  if (value > max) {
    return 0;
  }
  return value;
}

export function mergeDateAndTime(date: Date, hours: number, minutes: number, seconds: number): Date {
  const merged = new Date(date);
  merged.setHours(hours, minutes, seconds, 0);
  return merged;
}

export function mergeZonedDateAndTime(
  date: Date,
  hours: number,
  minutes: number,
  seconds: number,
  timeZone: string,
): Date {
  const parts = getZonedParts(date, timeZone);
  return zonedPartsToUtcDate({ ...parts, hours, minutes, seconds }, timeZone);
}

export function formatEventDateDisplay(date: Date, timeZone?: string | null): string {
  const parts = timeZone ? getZonedParts(date, timeZone) : localDateTimeParts(date);
  return `${padTimeUnit(parts.month)}/${padTimeUnit(parts.day)}/${parts.year}`;
}

export function formatEventDateTimeDisplay(
  date: Date,
  timeZoneAbbreviation?: string,
  timeZone?: string | null,
): string {
  const parts = timeZone ? getZonedParts(date, timeZone) : localDateTimeParts(date);
  const body = `${padTimeUnit(parts.month)}/${padTimeUnit(parts.day)}/${parts.year} ${padTimeUnit(parts.hours)}:${padTimeUnit(parts.minutes)}:${padTimeUnit(parts.seconds)}`;
  const explicit = timeZoneAbbreviation?.trim();
  if (explicit) {
    return `${body} ${explicit}`;
  }
  if (timeZone) {
    const derived = shortTimeZoneAbbreviation(date, timeZone);
    return derived ? `${body} ${derived}` : body;
  }
  return `${body} CT`;
}

/** DST letter abbreviation (`CDT`); `null` for GMT/UTC/unknown. */
export function shortTimeZoneAbbreviation(date: Date, timeZone: string): string | null {
  const raw =
    new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value ?? '';
  const letters = raw.replace(/[^A-Za-z]/g, '');
  if (!letters || letters === 'GMT' || letters === 'UTC') {
    return null;
  }
  return letters;
}

export function getZonedParts(date: Date, timeZone: string): EventqZonedDateTimeParts {
  const formatted = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const read = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(formatted.find((part) => part.type === type)?.value ?? '0');
  const hours = read('hour');
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hours: hours === 24 ? 0 : hours,
    minutes: read('minute'),
    seconds: read('second'),
  };
}

export function zonedPartsToUtcDate(parts: EventqZonedDateTimeParts, timeZone: string): Date {
  const utcGuess = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hours,
    parts.minutes,
    parts.seconds,
  );
  const firstOffset = zonedOffsetMs(new Date(utcGuess), timeZone);
  let instant = utcGuess - firstOffset;
  const secondOffset = zonedOffsetMs(new Date(instant), timeZone);
  if (secondOffset !== firstOffset) {
    instant = utcGuess - secondOffset;
  }
  return new Date(instant);
}

function localDateTimeParts(date: Date): EventqZonedDateTimeParts {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hours: date.getHours(),
    minutes: date.getMinutes(),
    seconds: date.getSeconds(),
  };
}

function zonedOffsetMs(date: Date, timeZone: string): number {
  const parts = getZonedParts(date, timeZone);
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hours,
    parts.minutes,
    parts.seconds,
  );
  return asUtc - date.getTime();
}

function zonedWeekday(year: number, month: number, day: number, timeZone: string): number {
  const instant = zonedPartsToUtcDate(
    { year, month, day, hours: 12, minutes: 0, seconds: 0 },
    timeZone,
  );
  const weekday =
    new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' })
      .formatToParts(instant)
      .find((part) => part.type === 'weekday')?.value ?? 'Sun';
  const index = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(weekday);
  return index >= 0 ? index : 0;
}

function civilDateFromUtcDay(
  year: number,
  monthIndex: number,
  day: number,
): { year: number; month: number; day: number } {
  const utc = new Date(Date.UTC(year, monthIndex, day));
  return {
    year: utc.getUTCFullYear(),
    month: utc.getUTCMonth() + 1,
    day: utc.getUTCDate(),
  };
}

function buildZonedCalendarWeeks(
  viewMonth: Date,
  selected: Date | null,
  min: Date | null | undefined,
  max: Date | null | undefined,
  timeZone: string,
): EventqCalendarCell[][] {
  const year = viewMonth.getFullYear();
  const monthIndex = viewMonth.getMonth();
  const month = monthIndex + 1;
  const weekday = zonedWeekday(year, month, 1, timeZone);
  const todayParts = getZonedParts(new Date(), timeZone);
  const selectedParts = selected ? getZonedParts(selected, timeZone) : null;
  const weeks: EventqCalendarCell[][] = [];

  for (let weekIndex = 0; weekIndex < 6; weekIndex++) {
    const week: EventqCalendarCell[] = [];
    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      const civil = civilDateFromUtcDay(year, monthIndex, 1 - weekday + weekIndex * 7 + dayIndex);
      const cellDate = zonedPartsToUtcDate(
        {
          year: civil.year,
          month: civil.month,
          day: civil.day,
          hours: 12,
          minutes: 0,
          seconds: 0,
        },
        timeZone,
      );
      week.push({
        date: cellDate,
        day: civil.day,
        inMonth: civil.year === year && civil.month === month,
        isSelected: selectedParts
          ? civil.year === selectedParts.year &&
            civil.month === selectedParts.month &&
            civil.day === selectedParts.day
          : false,
        isToday:
          civil.year === todayParts.year &&
          civil.month === todayParts.month &&
          civil.day === todayParts.day,
        disabled: isCalendarDayDisabled(cellDate, min, max, timeZone),
      });
    }
    weeks.push(week);
  }

  return weeks;
}
